import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../../common/block-text/block-text.component';
import { NotificationComponent } from '../../../common/notification/notification.component';
import { PinComponent } from '../../../common/pin/pin.component';
import { BaseVariableListBlockComponent } from '../../../common/base-variable-list-block.component';
import { CreativeFormat, CreativeImageRouterHelper } from '../../../../../utils/creative-image-router-helper';

/** One size Performance Max takes, with its per-size cap and Google's recommendation. */
interface PmaxImageSize {
  format: CreativeFormat;
  label: string;
  max: number;
  recommended: number;
}

/** The size's counter line, as the template renders it. */
interface PmaxImageSizeCount extends PmaxImageSize {
  count: number;
}

@Component({
    selector: 'app-pmax-image',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        BlockTextComponent,
        NotificationComponent,
        PinComponent,
    ],
    templateUrl: './pmax-image.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './pmax-image.component.scss'
})
export class PmaxImageComponent extends BaseVariableListBlockComponent {
  protected readonly variablePrefix = 'PmaxImage';
  protected readonly minVariables = 1;
  // 20 per size across the three sizes; the per-size cap is shown by the counter.
  readonly maxVariables = 60;

  readonly sizes: readonly PmaxImageSize[] = [
    { format: 'landscape', label: 'Landscape 1200x628', max: 20, recommended: 4 },
    { format: 'square', label: 'Square 1200x1200', max: 20, recommended: 4 },
    { format: 'portrait', label: 'Portrait 960x1200', max: 20, recommended: 2 },
  ];

  /**
   * Uploaded images per size. Measuring is async, so an image counts only once
   * it has loaded — the next change-detection pass picks it up.
   */
  get sizeCounts(): PmaxImageSizeCount[] {
    const formats = this.keys
      .map((key) => this.block.variables?.[key] ?? '')
      .filter((url) => url.trim())
      .map((url) => CreativeImageRouterHelper.formatOf(url));

    return this.sizes.map((size) => ({
      ...size,
      count: formats.filter((format) => format === size.format).length,
    }));
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: string): void {
    if (this.block.variables) {
      this.block.variables[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }

  onContainerClick(event: MouseEvent): void {
    // Forward the click event to the child app-pin component
    this.pinComponent.onContainerClick(event);
  }
}
